/**
 * Static chat backlog rendered inside the tutorial's chat panel.
 *
 * The lobby `ChatPanel` short-circuits its SDK chat call when a
 * `TutorialContext` is detected and renders this scripted backlog
 * instead — the tutorial wallet has no real seat / verify token, so
 * the SDK chat would otherwise fall through to the holder-gate
 * banner. Replacing the body lets the `holder-chat` step land on a
 * realistic-looking conversation between board holders without
 * touching the live chat plumbing.
 *
 * Authors are sourced from `FAKE_HOLDERS` (the same roster that fills
 * the tutorial board's tiles), with avatars generated via
 * `generateGridAvatarUrl` so each speaker reads as a unique player.
 * Timestamps are anchored at module load time and back-dated so the
 * scroll lands at the bottom on a fresh, recent conversation rather
 * than messages from an arbitrary past.
 */

import type { ChatMessageData } from '@/components/chat/chat-message';
import { FAKE_HOLDERS } from '@/components/tutorial/constants';
import { generateGridAvatarUrl } from '@/lib/avatar';

interface ScriptedLine {
  /** Index into `FAKE_HOLDERS` — keeps the speaker stable across rebuilds. */
  authorIdx: number;
  /** Seconds before "now" the message was sent. The list is ordered
   *  oldest → newest by message position; we use this offset to
   *  back-date `created_at` so the conversation reads as recent. */
  agoSeconds: number;
  message: string;
  /** Optional reply target — index into the same `LINES` array. The
   *  builder resolves this to a `ReplyToData` payload so the message
   *  renders the threaded preview the SDK chat shows for replies. */
  replyTo?: number;
  /** Optional `is_creator` flag so one of the seats reads as the
   *  board's author (mirrors how the live chat surfaces creators). */
  isCreator?: boolean;
}

/** Hand-tuned conversation between tutorial-board holders. Tone leans
 *  into takeover-game banter — strategy talk, market making, alpha
 *  calls, defending tiles, light shitposting. ~40 messages so the
 *  panel doesn't read as sparse on first paint. */
const LINES: ScriptedLine[] = [
  { authorIdx: 0, agoSeconds: 4 * 3600, message: 'gm board', isCreator: true },
  { authorIdx: 3, agoSeconds: 4 * 3600 - 60, message: 'gm 🫡' },
  { authorIdx: 7, agoSeconds: 4 * 3600 - 220, message: 'rents look healthy this week, who is running comps' },
  { authorIdx: 12, agoSeconds: 4 * 3600 - 380, message: 'i ran comps yday — uniswap pair is doing 80% of the rewards rn' },
  { authorIdx: 12, agoSeconds: 4 * 3600 - 379, message: 'flaunch is the swing factor though' },
  { authorIdx: 4, agoSeconds: 4 * 3600 - 540, message: 'I keep saying it — TUTOR coin is gonna pump again, mcap is silly cheap' },
  { authorIdx: 0, agoSeconds: 4 * 3600 - 700, message: 'agreed. we shipped a new gauge weight last night so the aerodrome side is going up too', isCreator: true },
  { authorIdx: 18, agoSeconds: 3 * 3600, message: 'someone defended floor lol' },
  { authorIdx: 22, agoSeconds: 3 * 3600 - 75, message: 'wasn\'t me 😅' },
  { authorIdx: 9, agoSeconds: 3 * 3600 - 220, message: 'price floor up to like $5.40 average now' },
  { authorIdx: 9, agoSeconds: 3 * 3600 - 219, message: 'this is the way' },
  { authorIdx: 33, agoSeconds: 3 * 3600 - 420, message: 'when is next batch of seats unlocking' },
  { authorIdx: 0, agoSeconds: 3 * 3600 - 600, message: 'no new seats — fixed supply. only way to get one is buyout', isCreator: true },
  { authorIdx: 33, agoSeconds: 3 * 3600 - 599, message: 'noted 🫡' },
  { authorIdx: 14, agoSeconds: 2 * 3600 - 120, message: 'just took over 0x...A1c4. felt overpriced for that holder tbh' },
  { authorIdx: 14, agoSeconds: 2 * 3600 - 119, message: 'gonna list at 1.2x and see what happens' },
  { authorIdx: 11, agoSeconds: 2 * 3600 - 220, message: 'classic. floor sweeper era' },
  { authorIdx: 28, agoSeconds: 2 * 3600 - 360, message: 'anyone else getting paged by the rewards stream every 5 min lol' },
  { authorIdx: 28, agoSeconds: 2 * 3600 - 359, message: 'cant do anything else' },
  { authorIdx: 6, agoSeconds: 2 * 3600 - 480, message: 'turn off notifs ser' },
  { authorIdx: 28, agoSeconds: 2 * 3600 - 478, message: 'never', replyTo: 19 },
  { authorIdx: 17, agoSeconds: 1 * 3600 - 200, message: 'deposit just dropped, cost of holding $0.30/wk for a $6 listing right?' },
  { authorIdx: 12, agoSeconds: 1 * 3600 - 175, message: 'yes 5%/week of self-assessed price', replyTo: 21 },
  { authorIdx: 17, agoSeconds: 1 * 3600 - 170, message: 'easy math, ty' },
  { authorIdx: 31, agoSeconds: 1 * 3600 - 120, message: 'feels like the cheap band is filling out. rents are about to compound' },
  { authorIdx: 19, agoSeconds: 50 * 60, message: 'wen new yield adapter' },
  { authorIdx: 0, agoSeconds: 48 * 60, message: 'next week. testing on staging now', isCreator: true },
  { authorIdx: 19, agoSeconds: 47 * 60, message: 'lfg' },
  { authorIdx: 36, agoSeconds: 42 * 60, message: 'whoever owns the corner tile, you printing fr' },
  { authorIdx: 5, agoSeconds: 35 * 60, message: 'corner is mine 😎' },
  { authorIdx: 36, agoSeconds: 34 * 60, message: 'pls list higher, im trying to make money' },
  { authorIdx: 5, agoSeconds: 33 * 60, message: 'never selling 💎🙌' },
  { authorIdx: 25, agoSeconds: 28 * 60, message: 'patient capital wins this game' },
  { authorIdx: 41, agoSeconds: 22 * 60, message: 'just claimed rewards. felt good 🤝' },
  { authorIdx: 13, agoSeconds: 18 * 60, message: 'small tip — claim every 24h, the gas spread is insignificant vs compounding' },
  { authorIdx: 8, agoSeconds: 13 * 60, message: 'is anyone running a discord for board holders?' },
  { authorIdx: 0, agoSeconds: 12 * 60, message: 'this chat is the discord 😄', isCreator: true },
  { authorIdx: 8, agoSeconds: 11 * 60, message: 'fair' },
  { authorIdx: 27, agoSeconds: 7 * 60, message: 'price floor pressure incoming, eyes on the cheap band' },
  { authorIdx: 24, agoSeconds: 4 * 60, message: 'I see it. someone\'s been pushing 1.3x lately on the bottom row' },
  { authorIdx: 29, agoSeconds: 90, message: 'time to stop dipping in and start coordinating' },
  { authorIdx: 2, agoSeconds: 30, message: 'ok who\'s defending and who\'s flipping. lets carve up the floor' },
];

/** Build the `ChatMessageData[]` payload the lobby chat panel renders.
 *  Memoised at module scope so the heavy work happens once per session
 *  — the panel mounts/unmounts as the tutorial layout reflows but the
 *  underlying messages don't need to be regenerated. */
function buildTutorialChatMessages(): ChatMessageData[] {
  const nowMs = Date.now();
  const out: ChatMessageData[] = [];
  // Push oldest first so the scroller lands with the most recent
  // message at the bottom (the SDK chat orders messages newest-last
  // before rendering).
  for (let i = 0; i < LINES.length; i++) {
    const line = LINES[i];
    const author = FAKE_HOLDERS[line.authorIdx % FAKE_HOLDERS.length];
    if (!author) continue;
    const created = nowMs - line.agoSeconds * 1000;
    let replyTo: ChatMessageData['reply_to'] = undefined;
    if (typeof line.replyTo === 'number') {
      const target = LINES[line.replyTo];
      if (target) {
        const targetAuthor = FAKE_HOLDERS[target.authorIdx % FAKE_HOLDERS.length];
        if (targetAuthor) {
          replyTo = {
            id: line.replyTo + 1,
            address: targetAuthor.address.toLowerCase(),
            display_name: targetAuthor.name,
            message: target.message,
          };
        }
      }
    }
    out.push({
      id: i + 1,
      address: author.address.toLowerCase(),
      message: line.message,
      // ChatMessage's `formatTimeAgo` expects ms.
      created_at: created,
      edited_at: null,
      display_name: author.name,
      avatar_url: generateGridAvatarUrl(author.address, 96),
      level: 1 + (line.authorIdx % 6),
      held_since: created - 7 * 86_400_000,
      reactions: [],
      reply_to: replyTo ?? null,
      is_creator: line.isCreator === true,
      is_dev: false,
    });
  }
  return out;
}

export const TUTORIAL_CHAT_MESSAGES: ChatMessageData[] = buildTutorialChatMessages();
